import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Plus, Search, SlidersHorizontal, X } from "lucide-react";
import { toast } from "sonner";
import { ModuleShell, DataTable, StatusBadge, ExportButtons, Field } from "@/components/partners/common";
import PartnerForm, { Sel } from "@/components/partners/PartnerForm";
import { KIND, fetchMeta, fmtMoney, titleCase, apiError } from "@/lib/partnerApi";

const ADV = {
  supplier: ["code", "name", "gstin", "pan", "contact", "mobile", "email", "city", "state", "category", "partner_type", "assigned_buyer", "priority", "approval_status"],
  dealer: ["code", "name", "gstin", "pan", "contact", "mobile", "email", "city", "state", "category", "partner_type", "territory", "sales_zone", "assigned_salesperson", "price_level", "approval_status"],
};
const LABEL = { gstin: "GSTIN", pan: "PAN", partner_type: "Type", assigned_buyer: "Buyer", assigned_salesperson: "Salesperson", approval_status: "Approval", price_level: "Price Level", sales_zone: "Sales Zone" };

export default function PartnerList({ kind }) {
  const k = KIND[kind];
  const nav = useNavigate();
  const [meta, setMeta] = useState(null);
  const [rows, setRows] = useState([]);
  const [q, setQ] = useState("");
  const [status, setStatus] = useState("");
  const [adv, setAdv] = useState({});
  const [showAdv, setShowAdv] = useState(false);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { ...Object.fromEntries(Object.entries(adv).filter(([, v]) => v)), q: q || undefined, status: status || undefined };
      setRows((await api.get(k.api, { params })).data);
    } catch (e) {
      toast.error(apiError(e, "Could not load"));
    } finally {
      setLoading(false);
    }
  }, [k.api, q, status, adv]);

  useEffect(() => { fetchMeta(kind).then(setMeta).catch((e) => toast.error(apiError(e))); }, [kind]);
  useEffect(() => { const t = setTimeout(load, 250); return () => clearTimeout(t); }, [load]);

  const counts = useMemo(() => rows.reduce((a, r) => ({ ...a, [r.master_status]: (a[r.master_status] || 0) + 1 }), {}), [rows]);
  const can = (a) => (meta?.my_actions || []).includes(a);

  const columns = [
    { key: "code", label: "Code", render: (r) => <span className="mono font-semibold text-slate-800">{r.code}</span>, exportable: true },
    { key: "name", label: "Name", render: (r) => (
      <div>
        <div className="text-slate-800 font-medium">{r.name}</div>
        {r.legal_name && r.legal_name !== r.name && <div className="text-[11px] text-slate-500">{r.legal_name}</div>}
      </div>) },
    { key: "partner_type", label: "Type" },
    { key: "category", label: "Category" },
    { key: "gstin", label: "GSTIN", render: (r) => <span className="mono">{r.gstin || "—"}</span> },
    { key: "contact", label: "Contact", render: (r) => (
      <div>{r.contact_person || r.contact || "—"}<div className="text-[11px] text-slate-500">{r.phone}</div></div>) },
    { key: "city", label: "City / State", render: (r) => `${r.city || r.billing_city || "—"}${(r.state || r.billing_state) ? `, ${r.state || r.billing_state}` : ""}` },
    ...(kind === "dealer"
      ? [{ key: "territory", label: "Territory" }, { key: "assigned_salesperson", label: "Salesperson" },
        { key: "credit_limit", label: "Credit Limit", type: "money", render: (r) => (r.financials_hidden ? "—" : fmtMoney(r.commercial?.credit_limit || r.credit_limit, r.commercial?.currency || "INR")) }]
      : [{ key: "payment_terms", label: "Payment Terms", render: (r) => r.payment_terms || titleCase(r.commercial?.payment_terms) || "—" },
        { key: "rating", label: "Rating", type: "number" }]),
    { key: "master_status", label: "Status", render: (r) => <StatusBadge value={r.master_status} tid={`status-${r.code}`} /> },
    { key: "approval_status", label: "Approval", render: (r) => <StatusBadge value={r.approval_status} /> },
  ];
  const exportCols = columns.map((c) => ({ key: c.key, label: c.label, type: c.type }));
  const exportRows = rows.map((r) => ({ ...r, contact: r.contact_person || r.contact, city: r.city || r.billing_city,
    payment_terms: r.payment_terms || r.commercial?.payment_terms, credit_limit: r.commercial?.credit_limit ?? r.credit_limit }));

  return (
    <ModuleShell
      kind={kind}
      tid={`${kind}-list-page`}
      title={`${k.label} Master`}
      subtitle={kind === "supplier"
        ? "Vendors with contacts, addresses, bank, tax, commercial terms, item mapping, documents, approvals and performance."
        : "Dealers & customers with contacts, delivery locations, pricing, credit control, stock, documents, approvals and performance."}
      actions={(
        <>
          {can("export") && <ExportButtons kind={kind} title={`${k.label} Master`} columns={exportCols} rows={exportRows} />}
          {can("create") && (
            <Button className="bg-blue-600 hover:bg-blue-700" onClick={() => setOpen(true)} data-testid={`new-${kind}-btn`}>
              <Plus className="w-4 h-4 mr-1.5" /> New {k.label}
            </Button>
          )}
        </>
      )}
    >
      <div className="flex flex-wrap gap-2">
        {["", "active", "under_review", "inactive", "blocked"].map((s) => (
          <button key={s || "all"} type="button" onClick={() => setStatus(s)}
            className={`text-xs px-3 py-1.5 rounded-full border ${status === s ? "bg-blue-600 text-white border-blue-600" : "bg-white text-slate-600 border-slate-200 hover:border-slate-300"}`}>
            {s ? titleCase(s) : "All"}{!status && s && counts[s] ? ` · ${counts[s]}` : ""}
          </button>
        ))}
      </div>
      <div className="flex items-end gap-3 flex-wrap">
        <div className="relative flex-1 min-w-[260px] max-w-xl">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 pointer-events-none" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} className="pl-9" data-testid={`${kind}-search`}
            placeholder="Search code, name, GSTIN, PAN, contact, mobile, email, city…" />
        </div>
        <Button variant="outline" size="sm" onClick={() => setShowAdv((v) => !v)} data-testid="adv-filter-btn">
          <SlidersHorizontal className="w-4 h-4 mr-1.5" /> Advanced filters{Object.values(adv).filter(Boolean).length ? ` (${Object.values(adv).filter(Boolean).length})` : ""}
        </Button>
        {Object.values(adv).some(Boolean) && (
          <Button variant="ghost" size="sm" onClick={() => setAdv({})}><X className="w-4 h-4 mr-1" /> Clear</Button>
        )}
        <div className="text-xs text-slate-500 mono ml-auto">{loading ? "Loading…" : `${rows.length} ${k.plural.toLowerCase()}`}</div>
      </div>
      {showAdv && meta && (
        <div className="bg-white border border-slate-200 rounded-lg p-4 grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3" data-testid="adv-filters">
          {ADV[kind].map((f) => (
            <Field key={f} label={LABEL[f] || titleCase(f)}>
              {f === "partner_type" ? <Sel value={adv[f]} onChange={(v) => setAdv({ ...adv, [f]: v })} options={meta.partner_types} placeholder="Any" />
                : f === "approval_status" ? <Sel value={adv[f]} onChange={(v) => setAdv({ ...adv, [f]: v })} options={["draft", "pending", "approved", "rejected"]} placeholder="Any" />
                  : f === "price_level" ? <Sel value={adv[f]} onChange={(v) => setAdv({ ...adv, [f]: v })} options={meta.price_levels} placeholder="Any" />
                    : f === "priority" ? <Sel value={adv[f]} onChange={(v) => setAdv({ ...adv, [f]: v })} options={meta.priorities} placeholder="Any" />
                      : f.startsWith("assigned_") ? <Sel value={adv[f]} onChange={(v) => setAdv({ ...adv, [f]: v })} options={(meta.users || []).map((u) => ({ value: u.email, label: u.name || u.email }))} placeholder="Any" />
                        : <Input value={adv[f] || ""} onChange={(e) => setAdv({ ...adv, [f]: e.target.value })} className="h-9" />}
            </Field>
          ))}
        </div>
      )}
      <DataTable columns={columns} rows={rows} tid={`${kind}-table`} onRowClick={(r) => nav(`${k.base}/${r.id}`)}
        empty={loading ? "Loading…" : `No ${k.plural.toLowerCase()} match.`} />
      <PartnerForm kind={kind} meta={meta} open={open} onOpenChange={setOpen} partners={rows}
        onSaved={(d) => nav(`${k.base}/${d.id}`)} />
    </ModuleShell>
  );
}
